import React, { useState } from "react";
import { Alert, Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { SyncAlt } from "@mui/icons-material";
import { disconnectTeamsAccount, startTeamsConnect } from "../../api/ProcurementClient";
import { ChatConversation } from "../../models/Chat";
import { useChat } from "./ChatProvider";

/** Teams brand purple, used only for the small sync markers. */
export const TEAMS_PURPLE = "#5b5fc7";

async function goToMicrosoftSignIn(): Promise<void> {
  const { authorizeUrl } = await startTeamsConnect(window.location.href);
  window.location.assign(authorizeUrl);
}

/** Footer of the conversation list: connect / disconnect this user's Teams account. */
export const TeamsConnectionPanel: React.FC = () => {
  const { teams, setTeams } = useChat();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!teams?.enabled) return null;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Teams request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider", px: 1.5, py: 1, bgcolor: "#ebebeb" }}>
      {teams.connected ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <SyncAlt sx={{ fontSize: 18, color: TEAMS_PURPLE }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} component="div">
              Synced with Microsoft Teams
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {teams.email || teams.displayName}
            </Typography>
          </Box>
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() =>
              void run(async () => setTeams(await disconnectTeamsAccount()))
            }
          >
            Disconnect
          </Button>
        </Stack>
      ) : (
        <Stack gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            Connect your Microsoft account so direct messages also appear in Teams, and Teams
            replies show up here.
          </Typography>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            startIcon={<SyncAlt />}
            onClick={() => void run(goToMicrosoftSignIn)}
            sx={{ bgcolor: TEAMS_PURPLE, "&:hover": { bgcolor: "#4f52b2" }, alignSelf: "flex-start" }}
          >
            Connect Microsoft Teams
          </Button>
        </Stack>
      )}
      {(error || teams.lastError) && (
        <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: "0.75rem" }}>
          {error || teams.lastError}
        </Alert>
      )}
    </Box>
  );
};

/** Small header badge on direct chats explaining whether they sync with Teams. */
export const TeamsConversationBadge: React.FC<{ conversation: ChatConversation }> = ({
  conversation,
}) => {
  const { teams } = useChat();
  if (!teams?.enabled || conversation.type !== "direct") return null;

  let label = "Teams";
  let tooltip: string;
  if (conversation.teamsLinked && teams.connected) {
    tooltip = "Synced with your Microsoft Teams chat. Messages here and in Teams show up in both.";
  } else if (conversation.teamsLinked) {
    tooltip =
      "Linked to Teams. Teams replies show up here; connect your own Teams account (bottom of the chat list) so your messages also go to Teams.";
    label = "Teams (connect to send)";
  } else if (teams.connected) {
    tooltip = "This chat will link to Microsoft Teams when you send a message.";
  } else {
    return null;
  }

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        icon={<SyncAlt sx={{ fontSize: 14 }} />}
        label={label}
        variant={conversation.teamsLinked ? "filled" : "outlined"}
        sx={{
          height: 22,
          fontSize: "0.7rem",
          color: conversation.teamsLinked ? "#fff" : TEAMS_PURPLE,
          bgcolor: conversation.teamsLinked ? TEAMS_PURPLE : undefined,
          borderColor: TEAMS_PURPLE,
          "& .MuiChip-icon": { color: "inherit" },
        }}
      />
    </Tooltip>
  );
};
